import { ChangeEvent, useState, useId } from 'react';
import styles from './SubscriptionForm.module.scss';
import { TextInput, TextInputProps } from '../../forms/TextInput';

type RenderSubscriptionFormInputOptions = {
  id?: string;
  isDisabled?: boolean;
  isErrored?: boolean;
  isRequired?: boolean;
  isFullWidth?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
};

type RenderSubscriptionFormButtonOptions = {
  size?: 'medium';
};

export type Props = {
  /**
   * Optional error message for the input field. If it exists, the field will be
   * considered to have an error.
   */
  inputError?: string;
  /**
   * Is the input full width?
   * @default false
   */
  inputIsFullWidth?: boolean;
  /**
   * `id` for the input field. If this isn't supplied then one will be generated
   * to properly link the `label` to the input.
   */
  inputId?: string;
  /**
   * Indicates whether the field is disabled.
   */
  inputIsDisabled?: boolean;
  /**
   * Indicates whether the field is required.
   */
  inputIsRequired?: boolean;
  /**
   * Text for the `placeholder` of the input field.
   */
  placeholderText: string;
  /**
   * Make input field fluid in the container.
   */
  fluidInput?: boolean;
  /**
   * Rendered button field.
   */
  renderButton: (opts: RenderSubscriptionFormButtonOptions) => React.ReactNode;
  /**
   * Function that returns the rendered input field.
   */
  renderInput?: (opts: RenderSubscriptionFormInputOptions) => React.ReactNode;
  /**
   * Function that gets called on form submit.
   */
  onSubmit?: (subscriptionValue?: string) => void;
};

/**
 * Wraps a `form` element and renders an `input` field and `submit` button.
 */
export const SubscriptionForm = ({
  inputIsFullWidth = true,
  fluidInput = false,
  ...props
}: Props) => {
  const generatedInputId = useId();

  const id = props.inputId || generatedInputId;

  const [subscribedValue, setSubscribedValue] = useState<string>('');

  const inputSize: TextInputProps['size'] = 'medium';

  const inputProps = {
    id,
    isDisabled: props.inputIsDisabled,
    isErrored: !!props.inputError,
    isRequired: props.inputIsRequired,
    isFullWidth: inputIsFullWidth,
    placeholder: props.placeholderText,
    ariaLabel: props.placeholderText,
    size: inputSize,
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      setSubscribedValue(event.target.value),
  };

  return (
    <form
      onSubmit={() => props.onSubmit?.(subscribedValue)}
      data-testid="subscription-form"
      data-zds
    >
      <div className={styles.root} data-zds>
        <div
          className={styles.inputField}
          data-fluid-input={fluidInput ? true : undefined}
          data-zds
        >
          {props.renderInput ? (
            props.renderInput(inputProps)
          ) : (
            <TextInput {...inputProps} />
          )}
        </div>
        {props.renderButton({ size: 'medium' })}
      </div>
    </form>
  );
};
